import React, { useMemo, useState } from "react";
import { DailyUsage } from "../../../types/llm-usage.types";
import { format, parseISO } from "date-fns";
import { formatCurrency } from "../../../utils/formatters";

interface DailyTrendChartProps {
  data: DailyUsage[];
}

const formatDate = (dateStr: string): string => {
  try {
    const date = parseISO(dateStr);
    return format(date, "MMM d");
  } catch {
    return dateStr;
  }
};

function computeMovingAverage(data: DailyUsage[], window: number): (number | null)[] {
  return data.map((_, i) => {
    if (i < window - 1) return null;
    let sum = 0;
    for (let j = i - window + 1; j <= i; j++) {
      sum += data[j].totalCostUsd;
    }
    return sum / window;
  });
}

export const DailyTrendChart: React.FC<DailyTrendChartProps> = ({ data }) => {
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);

  const { maxCost, bars, movingAvg } = useMemo(() => {
    if (!data.length) return { maxCost: 0, bars: [], movingAvg: [] };

    const ma = computeMovingAverage(data, 7);
    const allValues = [
      ...data.map((d) => d.totalCostUsd),
      ...ma.filter((v): v is number => v !== null),
    ];
    const max = Math.max(...allValues);

    const calculatedBars = data.map((item) => ({
      ...item,
      percentage: max > 0 ? (item.totalCostUsd / max) * 100 : 0,
    }));

    return { maxCost: max, bars: calculatedBars, movingAvg: ma };
  }, [data]);

  if (!data.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">
          Trend kosztów dziennych
        </h3>
        <div className="text-neutral-500 text-center py-8">
          Brak danych dla wybranego okresu
        </div>
      </div>
    );
  }

  // Build SVG polyline points for the moving average
  const maPoints = bars
    .map((bar, i) => {
      const val = movingAvg[i];
      if (val === null) return null;
      // x: center of each bar slot; y: inverted percentage
      const x = ((i + 0.5) / bars.length) * 100;
      const y = maxCost > 0 ? 100 - (val / maxCost) * 100 : 100;
      return `${x},${y}`;
    })
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-6"
      data-testid="chart-daily-trend"
      role="figure"
      aria-label="Wykres trendu dziennych kosztów"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-neutral-900">
          Trend kosztów dziennych
        </h3>
        {maPoints && (
          <div className="flex items-center gap-1.5 text-xs text-neutral-500">
            <span className="inline-block w-4 h-0.5 bg-red-500 rounded" />
            <span>Średnia 7d</span>
          </div>
        )}
      </div>

      <div className="relative flex gap-1 h-48">
        {/* Y-axis */}
        <div className="flex flex-col justify-between text-xs text-neutral-500 pr-2 py-0.5">
          <span>{formatCurrency(maxCost)}</span>
          <span>{formatCurrency(maxCost / 2)}</span>
          <span>$0</span>
        </div>

        {/* Chart area */}
        <div className="flex-1 relative h-full">
          {/* Bars */}
          <div className="flex items-end gap-1 h-full">
            {bars.map((bar, index) => (
              <div
                key={bar.date}
                className="flex-1 h-full flex flex-col justify-end items-center relative"
                data-testid={`bar-${index}`}
              >
                {/* Tooltip */}
                <div
                  className={`absolute bottom-full mb-2 z-10 transition-opacity ${
                    activeTooltip === index
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                  }`}
                >
                  <div className="bg-neutral-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    <div className="font-medium">{formatDate(bar.date)}</div>
                    <div>{formatCurrency(bar.totalCostUsd)}</div>
                    <div>{bar.count} rekordów</div>
                    {movingAvg[index] !== null && (
                      <div className="text-red-300">
                        Śr. 7d: {formatCurrency(movingAvg[index]!)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Bar */}
                <div
                  tabIndex={0}
                  role="img"
                  aria-label={`${formatDate(bar.date)}: ${formatCurrency(bar.totalCostUsd)}, ${bar.count} rekordów`}
                  className="w-full bg-primary-500 hover:bg-primary-600 focus:bg-primary-600 transition-colors rounded-t cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
                  style={{
                    height: `${Math.max(bar.percentage, 2)}%`,
                  }}
                  onMouseEnter={() => setActiveTooltip(index)}
                  onMouseLeave={() => setActiveTooltip(null)}
                  onFocus={() => setActiveTooltip(index)}
                  onBlur={() => setActiveTooltip(null)}
                  onTouchStart={() => setActiveTooltip(index)}
                />
              </div>
            ))}
          </div>

          {/* Moving average SVG overlay */}
          {maPoints && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <polyline
                points={maPoints}
                fill="none"
                stroke="#ef4444"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          )}
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between mt-2 text-xs text-neutral-500 ml-12">
        {bars.length > 0 && (
          <>
            <span>{formatDate(bars[0].date)}</span>
            {bars.length > 2 && (
              <span>
                {formatDate(bars[Math.floor(bars.length / 2)].date)}
              </span>
            )}
            {bars.length > 1 && (
              <span>{formatDate(bars[bars.length - 1].date)}</span>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default DailyTrendChart;
